import { forwardRef, useState } from "react";

import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

interface Country {
  code: string;
  name: string;
  flag: string;
}

const countries: Country[] = [
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "JP", name: "Japan", flag: "🇯🇵" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "IN", name: "India", flag: "🇮🇳" },
  { code: "MX", name: "Mexico", flag: "🇲🇽" },
];

export interface ULThemeCountryListProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  value?: string;
  onChange?: (countryCode: string) => void;
  placeholder?: string;
  fullWidth?: boolean;
  error?: boolean;
}

const ULThemeCountryList = forwardRef<
  HTMLButtonElement,
  ULThemeCountryListProps
>(
  (
    {
      value,
      onChange,
      placeholder = "Select a country",
      fullWidth = true,
      error = false,
      className,
      disabled,
      ...rest
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const selectedCountry = countries.find((country) => country.code === value);

    const handleSelect = (countryCode: string) => {
      onChange?.(countryCode);
      setIsOpen(false);
    };

    const baseStyles = cn(
      "inline-flex items-center justify-between px-4 py-4 text-left font-medium transition-colors duration-150 ease-in-out",
      "theme-universal:bg-input-bg",
      "theme-universal:text-input-text",
      "theme-universal:border",
      "theme-universal:border-input-border",
      "theme-universal:text-(length:--ul-theme-font-body-text-size)",
      "theme-universal:font-body",
      "theme-universal:rounded-input",
      "theme-universal:hover:border-base-focus",
      "theme-universal:focus:outline-none theme-universal:focus:ring-2 theme-universal:focus:ring-base-focus/20",
      error && "theme-universal:border-destructive"
    );

    const widthStyles = fullWidth ? "w-full" : "";
    const disabledStyles = disabled
      ? "disabled:opacity-70 cursor-not-allowed"
      : "cursor-pointer";

    const dropdownStyles = cn(
      "absolute z-50 w-full mt-1 max-h-60 overflow-auto",
      "theme-universal:bg-input-bg",
      "theme-universal:border",
      "theme-universal:border-input-border",
      "theme-universal:rounded-input",
      "shadow-lg"
    );

    const optionStyles = cn(
      "w-full px-4 py-3 text-left transition-colors duration-150 ease-in-out flex items-center",
      "theme-universal:text-input-text",
      "theme-universal:hover:bg-input-border/10",
      "theme-universal:focus:bg-input-border/10",
      "theme-universal:focus:outline-none",
      "cursor-pointer"
    );

    return (
      <div className={cn("relative", className)}>
        <button
          ref={ref}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(baseStyles, widthStyles, disabledStyles)}
          {...rest}
        >
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {selectedCountry ? (
              <>
                <div className="flex-shrink-0 w-6 h-4 flex items-center justify-center">
                  <span className="text-lg">{selectedCountry.flag}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="theme-universal:text-input-text text-dark theme-universal:font-body truncate">
                    {selectedCountry.name}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex-shrink-0 w-6 h-4 theme-universal:bg-input-border rounded-sm"></div>
                <span className="theme-universal:text-input-labels flex-1 truncate">
                  {placeholder}
                </span>
              </>
            )}
          </div>

          <div className="flex-shrink-0 ml-2">
            <ChevronDown
              className={cn(
                "w-4 h-4 theme-universal:text-input-labels transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            />
          </div>
        </button>

        {isOpen && (
          <div className={dropdownStyles}>
            {countries.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => handleSelect(country.code)}
                className={optionStyles}
              >
                <span className="mr-3 text-lg">{country.flag}</span>
                <span className="theme-universal:text-input-text">
                  {country.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

ULThemeCountryList.displayName = "ULThemeCountryList";

export default ULThemeCountryList;
